import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  ServiceHoursDefinition,
  WEEKDAYS,
  formatServiceHours,
  parseTimeInput,
} from "../sites/alarmContact";

type Props = {
  open: boolean;
  contactLabel: string;
  value: ServiceHoursDefinition[];
  onCancel: () => void;
  onSave: (next: ServiceHoursDefinition[]) => void;
};

export default function SendSmsHoursDialog({ open, contactLabel, value, onCancel, onSave }: Props) {
  const [windows, setWindows] = useState<ServiceHoursDefinition[]>([]);
  const [days, setDays] = useState<number[]>([]);
  const [from, setFrom] = useState<string>("08:00");
  const [to, setTo] = useState<string>("16:00");

  // Kun avhengig av `open`: forelderen sender en ny array-referanse hver render
  // (`sendSmsHours ?? []`), så `value` i deps ville gitt en uendelig render-løkke.
  // Vi vil uansett bare lese inn utgangspunktet når dialogen åpnes.
  useEffect(() => {
    if (open) {
      setWindows(value ?? []);
      setDays([]);
      setFrom("08:00");
      setTo("16:00");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const noDaysSelected = days.length === 0;
  // Backend sammenligner from/to innenfor samme dogn i Europe/Oslo
  // (ServiceHourUtils.ServiceHourOverlapWith), så vinduer som krysser midnatt
  // kan ikke uttrykkes.
  const invalidRange = !!from && !!to && to <= from;
  const canAdd = !noDaysSelected && !invalidRange && !!from && !!to;

  const addWindow = () => {
    if (!canAdd) return;
    setWindows((prev) => [
      ...prev,
      { days: [...days].sort((a, b) => a - b), from: parseTimeInput(from), to: parseTimeInput(to) },
    ]);
    setDays([]);
  };

  const removeWindow = (index: number) => {
    setWindows((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle>SMS-tider for {contactLabel || "mottaker"}</DialogTitle>
      <DialogContent>
        {windows.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Ingen tidsvinduer - mottakeren får SMS hele døgnet, alle dager.
          </Alert>
        ) : (
          <List dense>
            {windows.map((window, index) => (
              <ListItem
                key={index}
                secondaryAction={
                  <IconButton edge="end" size="small" onClick={() => removeWindow(index)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                }
              >
                <ListItemText primary={formatServiceHours(window)} />
              </ListItem>
            ))}
          </List>
        )}

        <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>
          Legg til tidsvindu
        </Typography>

        <ToggleButtonGroup
          value={days}
          onChange={(_event, next: number[]) => setDays(next)}
          size="small"
          sx={{ flexWrap: "wrap", mb: 2 }}
        >
          {WEEKDAYS.map((day) => (
            <ToggleButton key={day.value} value={day.value}>
              {day.short}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            label="Fra"
            type="time"
            size="small"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Til"
            type="time"
            size="small"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="outlined" size="small" onClick={addWindow} disabled={!canAdd}>
            Legg til
          </Button>
        </Box>

        {noDaysSelected && (
          <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
            Velg minst én ukedag - et vindu uten dager treffer aldri.
          </Typography>
        )}
        {invalidRange && (
          <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
            Til må være etter fra. Vinduer som krysser midnatt støttes ikke - del dem i to.
          </Typography>
        )}

        <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 2 }}>
          Tidene tolkes som norsk lokaltid (Europe/Oslo).
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Avbryt</Button>
        <Button variant="contained" onClick={() => onSave(windows)}>
          Ferdig
        </Button>
      </DialogActions>
    </Dialog>
  );
}
